import { useCallback, useEffect, useState } from 'react'

import { useSelector } from 'react-redux'

import { toast } from '@webapp/components/Notification'
import { useCurrencySelect } from '@webapp/core/components/CurrencySelect/useCurrencySelect'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { getExpenseItemAccountBudgetPeriodUseCase } from '@webapp/modules/expense/business-logic/use-cases/GetExpenseItemAccountBudgetPeriodUseCase'
import { getExpenseItemCorrectionFormDataUseCase } from '@webapp/modules/expense/business-logic/use-cases/GetExpenseItemCorrectionFormDataUseCase'
import { type ApprovedExpenseItem } from '@webapp/modules/expense/entities/ApprovedExpenseItem'
import { type BudgetPeriod } from '@webapp/platform/finance'
import messages from './messages'
import { useMutateCorrectExpenseItemAccountCodes } from '../../models/useMutateCorrectExpenseItemAccountCodes'
import { type CorrectExpenseItemAccountCodesFormSchema } from '../../views/CorrectExpenseItemAccountCodesForm'

interface UseCorrectExpenseItemAccountCodesPresenterParams {
  item: ApprovedExpenseItem
  onSuccess: () => void
}

export const useCorrectExpenseItemAccountCodesPresenter = ({
  item,
  onSuccess,
}: UseCorrectExpenseItemAccountCodesPresenterParams) => {
  const intl = useProcurifyIntl()

  const featureSwitches = useSelector(makeSelectFeatureSwitches())
  const flexibleSpendTrackingEnabled =
    featureSwitches.enable_flexible_spend_tracking

  const { correctAccountCodes, isLoading: isSaving } =
    useMutateCorrectExpenseItemAccountCodes()

  const formatMedia = useFormatMedia()

  // Cached currency lookup shared with CurrencySelect — amounts format with
  // the report-level currency (BHV-012)
  const {
    data: { currencies },
  } = useCurrencySelect()

  const currencyCode = currencies.find(
    (currency) => currency.id === item.currencyId
  )?.name

  const [siblings, setSiblings] = useState<ApprovedExpenseItem[]>([])
  const [isLoadingSiblings, setIsLoadingSiblings] = useState(false)
  const [budgetPeriodsByItemId, setBudgetPeriodsByItemId] = useState<
    Record<number, BudgetPeriod | undefined>
  >({})

  useEffect(() => {
    let isMounted = true

    // Siblings are fetched only when the row reports sibling items (BHV-016)
    if (!item.expenseReport.hasSiblingItems) return undefined

    setIsLoadingSiblings(true)

    getExpenseItemCorrectionFormDataUseCase
      .execute({
        reportId: item.expenseReport.id,
        excludeExpenseItemId: item.id,
      })
      .then((result) => {
        if (!isMounted) return
        setSiblings(result.siblings)
      })
      .catch((error) => {
        if (!isMounted) return
        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children:
            getMessageFromError(error) ??
            intl.formatMessage(messages.siblingsFetchErrorMessage),
        })
      })
      .finally(() => {
        if (!isMounted) return
        setIsLoadingSiblings(false)
      })

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.expenseReport.id, item.expenseReport.hasSiblingItems])

  const handleAccountSelected = async (
    expenseItemId: number,
    accountId: number
  ) => {
    // Budget Date is hidden under enable_flexible_spend_tracking — skip the
    // lookup entirely (BHV-020)
    if (flexibleSpendTrackingEnabled) return

    try {
      const budgetPeriod =
        await getExpenseItemAccountBudgetPeriodUseCase.execute({
          expenseItemId,
          accountId,
        })

      setBudgetPeriodsByItemId((previous) => ({
        ...previous,
        [expenseItemId]: budgetPeriod,
      }))
    } catch (error) {
      setBudgetPeriodsByItemId((previous) => ({
        ...previous,
        [expenseItemId]: undefined,
      }))

      toast.error({
        title: intl.formatMessage(messages.errorTitle),
        children:
          getMessageFromError(error) ??
          intl.formatMessage(messages.budgetPeriodFetchErrorMessage),
      })
    }
  }

  const handleSubmit = async (
    values: CorrectExpenseItemAccountCodesFormSchema
  ) => {
    const primaryAccount = values.accountsByItemId[item.id]

    if (!primaryAccount) return

    // Primary item first, then siblings (wire contract)
    const corrections = [
      { expenseItemId: item.id, accountId: primaryAccount.id },
      ...siblings.reduce<{ expenseItemId: number; accountId: number }[]>(
        (acc, sibling) => {
          const account = values.accountsByItemId[sibling.id]
          if (account) {
            acc.push({ expenseItemId: sibling.id, accountId: account.id })
          }
          return acc
        },
        []
      ),
    ]

    try {
      await correctAccountCodes(corrections)

      onSuccess()
    } catch (error) {
      // Failure keeps the form open and surfaces the parsed server error
      // (BHV-022, ADR-10)
      toast.error({
        title: intl.formatMessage(messages.errorTitle),
        children:
          getMessageFromError(error) ??
          intl.formatMessage(messages.saveErrorMessage),
      })
    }
  }

  const resolveAttachmentUrl = useCallback(
    (attachment: string) => formatMedia(attachment),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return {
    data: {
      siblings,
      budgetPeriodsByItemId,
      currencyCode,
    },
    states: {
      isLoadingSiblings,
      isSaving,
      showBudgetDate: !flexibleSpendTrackingEnabled,
      showApplyToAllAccount: !flexibleSpendTrackingEnabled,
    },
    handlers: {
      handleSubmit,
      handleAccountSelected,
      resolveAttachmentUrl,
    },
  }
}
